import React, { useState, useEffect, useRef } from 'react';
import { Divider, Input } from 'antd';
import FormItem from 'src/components/form-item';
import { noop } from 'lodash';
import './style.scss';
import SimpleSelect from 'src/components/simple-select';
import CITTreeSelect from 'src/components/tree-select';
import TreeSelectCutoff from 'src/components/tree-select-cutoff';
import CITSlider from 'src/components/slider';
import Button, { Type as ButtonType } from 'src/components/button';
import { CloseOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
const { Search } = Input;

/**
 * Renders a Filters component.
 * @param {Object} props - The component props.
 * @param {Function} props.onApiFilter - The function to apply API filters (fetch data from API with filters).
 * @param {{}} props.filters - The filters object with all filters' attributes.
 * @returns {JSX.Element} The rendered filters component.
 * @example
 * <Filters
 *   onApiFilter={handleSetApiFilter}
 *   filters={filtersFromApi}
 * />
 */
function Filters({ onApiFilter, filters }) {
  const [loginFilter, setLoginFilter] = useState('');
  const [fitStatus, setFitStatus] = useState([]);
  const [role, setRole] = useState([]);
  const [allianceAndGu, setAllianceAndGu] = useState([]);
  const [site, setSite] = useState([]);
  const [client, setClient] = useState([]);
  const [positionMap, setPositionMap] = useState([]);
  const [englishLevel, setEnglishLevel] = useState([]);
  const [autoDeclaration, setAutoDeclaration] = useState([]);
  const [skills, setSkills] = useState([]);
  const [cutoffPercent, setCutoffPercent] = useState([]);
  const [cutoffDate, setCutoffDate] = useState([]);
  const [pdm, setPdm] = useState([]);
  const [statusProfile, setStatusProfile] = useState([]);
  const [isEmptyDate, setIsEmptyDate] = useState(false);
  const [clearFilter, setClearFilter] = useState(false);
  const { t } = useTranslation();
  const statusOptions = [t('free'), 'Fit', 'Match'];
  const statusProfileOptions = [t('active'), t('inactive')];

  const apiFiltersRef = useRef({});

  const handleDropdownClose = (open) => {
    if (!open) onApiFilter(apiFiltersRef.current);
  };

  const handleDeselect = (filterKey, currentValues) => (removedValue) => {
    onApiFilter({
      ...apiFiltersRef.current,
      [filterKey]: currentValues.filter((v) => v !== removedValue),
    });
  };

  const handleSelect = (filterKey, currentValues) => (selectedValue) => {
    onApiFilter({
      ...apiFiltersRef.current,
      [filterKey]: [...currentValues, selectedValue],
    });
  };

  const handleClear = (filterKey) => () => {
    onApiFilter({ ...apiFiltersRef.current, [filterKey]: [] });
  };

  useEffect(() => {
    /**
     * Set filters' list value to empty upon triggering clear button.
     */
    if (clearFilter) {
      setLoginFilter('');
      setFitStatus([]);
      setRole([]);
      setAllianceAndGu([]);
      setSite([]);
      setClient([]);
      setPositionMap([]);
      setEnglishLevel([]);
      setAutoDeclaration([]);
      setSkills([]);
      setCutoffPercent([]);
      setCutoffDate([]);
      setPdm([]);
      setStatusProfile([]);
      onApiFilter({});
    }
    setClearFilter(false);
  }, [clearFilter]);

  const transformCutoffDate = (dates) =>
    dates
      .map((e) => {
        if (e.indexOf('-') === -1) {
          return filters.cutoffMonths
            .find((cm) => cm.year === Number(e))
            .months.map((c) => c.value);
        }
        return e;
      })
      .flat();

  const handleCutoffDateChange = (newValue) => {
    setCutoffDate(newValue);
    if (newValue.length < cutoffDate.length) {
      onApiFilter({
        ...apiFiltersRef.current,
        cutoffDate: transformCutoffDate(newValue),
      });
    }
  };

  useEffect(() => {
    /**
     * Call onApiFilter function upon changing API filters' values.
     */
    const cutoffDateFilter = cutoffDate.length === 0 ? [] : transformCutoffDate(cutoffDate);

    if (cutoffDate.length === 0) {
      setIsEmptyDate(true);
    } else {
      setIsEmptyDate(false);
    }

    const transformedFitStatus = fitStatus.map(status => {
      return status === t('free') ? 'Free' : status;
    });

    const transformedStatusProfile = statusProfile.map(s =>
      s === t('active') ? 'active' : 'inactive'
    );

    const apiFilters = {
      role,
      allianceAndGu,
      site,
      client,
      positionMap,
      englishLevel,
      autoDeclaration,
      skills,
      cutoffPercent,
      cutoffDate: cutoffDateFilter,
      pdm,
    };

    if (loginFilter) {
      apiFilters.login = loginFilter;
    }

    if (fitStatus.length > 0) {
      apiFilters.fitStatus = transformedFitStatus;
    }

    if (statusProfile.length > 0) {
      apiFilters.statusProfile = transformedStatusProfile;
    }

    apiFiltersRef.current = apiFilters;
  }, [
    loginFilter,
    fitStatus,
    role,
    allianceAndGu,
    site,
    client,
    positionMap,
    englishLevel,
    autoDeclaration,
    skills,
    cutoffPercent,
    cutoffDate,
    pdm,
    statusProfile,
    filters.cutoffMonths,
    t,
  ]);

  const handleFitStatusChange = (selectedValues) => {
    setFitStatus(Array.isArray(selectedValues) ? selectedValues : []);
  };

  return (
    <>
      <div className='profile-filter__container'>
        <FormItem label='Login' id='people-search-field'>
          <Search
            id='people-search-field'
            value={loginFilter}
            onChange={(e) => setLoginFilter(e.target.value)}
            onSearch={() => onApiFilter(apiFiltersRef.current)}
            placeholder={t('searchByLogin')}
            aria-label={t('loginFilter')}
            aria-labelledby='people-search-field'
          />
        </FormItem>
        <FormItem label={t('role')} id='role'>
          <SimpleSelect
            options={filters.role}
            onChange={setRole}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('role', role)}
            onDeselect={handleDeselect('role', role)}
            onClear={handleClear('role')}
            aria-label={t('roleFilter')}
            aria-labelledby='role'
          />
        </FormItem>
        <FormItem label='GU' id='growth-unit'>
          <CITTreeSelect
            options={filters.alliance}
            onChange={setAllianceAndGu}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onDeselect={handleDeselect('allianceAndGu', allianceAndGu)}
            onClear={handleClear('allianceAndGu')}
            aria-labelledby='growth-unit'
            aria-label={t('guFilter')}
          />
        </FormItem>
        <FormItem label={t('fitStatus')} id='fit-status'>
          <SimpleSelect
            options={statusOptions}
            onChange={handleFitStatusChange}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('fitStatus', fitStatus)}
            onDeselect={handleDeselect('fitStatus', fitStatus)}
            onClear={handleClear('fitStatus')}
            mode="multiple"
            value={fitStatus}
            aria-labelledby='fit-status'
            aria-label={t('fitStatusFilter')}
          />
        </FormItem>
        <FormItem label={t('site')} id='site'>
          <SimpleSelect
            options={filters.site}
            onChange={setSite}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('site', site)}
            onDeselect={handleDeselect('site', site)}
            onClear={handleClear('site')}
            aria-label={t('locationFilter')}
            aria-labelledby='site'
          />
        </FormItem>
        <FormItem label={t('currentClient')} id='current-client'>
          <SimpleSelect
            options={filters.client}
            onChange={setClient}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('client', client)}
            onDeselect={handleDeselect('client', client)}
            onClear={handleClear('client')}
            aria-label={t('currentClientFilter')}
            aria-labelledby='current-client'
          />
        </FormItem>
        <FormItem label={t('positionMap')} id='position-map'>
          <SimpleSelect
            options={filters.positionMap}
            onChange={setPositionMap}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('positionMap', positionMap)}
            onDeselect={handleDeselect('positionMap', positionMap)}
            onClear={handleClear('positionMap')}
            aria-label={t('positionMapFilter')}
            aria-labelledby='position-map'
          />
        </FormItem>
        <FormItem label={t('englishLevel')} id='english-level'>
          <SimpleSelect
            options={filters.englishLevel}
            onChange={setEnglishLevel}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('englishLevel', englishLevel)}
            onDeselect={handleDeselect('englishLevel', englishLevel)}
            onClear={handleClear('englishLevel')}
            aria-label={t('englishLevelFilter')}
            aria-labelledby='english-level'
          />
        </FormItem>
        <FormItem label={t('pwd')} id='pwd'>
          <SimpleSelect
            options={filters.personWithDisability}
            onChange={setAutoDeclaration}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('autoDeclaration', autoDeclaration)}
            onDeselect={handleDeselect('autoDeclaration', autoDeclaration)}
            onClear={handleClear('autoDeclaration')}
            aria-label={t('pwdFilter')}
            aria-labelledby='pwd'
          />
        </FormItem>
        <FormItem label={t('tech')} id='technology'>
          <SimpleSelect
            options={filters.skill}
            onChange={setSkills}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('skills', skills)}
            onDeselect={handleDeselect('skills', skills)}
            onClear={handleClear('skills')}
            aria-label={t('techFilter')}
            aria-labelledby='technology'
          />
        </FormItem>
        <FormItem label={t('pdm')} id='pdm'>
          <SimpleSelect
            options={filters.pdm}
            onChange={setPdm}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('pdm', pdm)}
            onDeselect={handleDeselect('pdm', pdm)}
            onClear={handleClear('pdm')}
            aria-label={t('pdmFilter')}
            aria-labelledby='pdm'
          />
        </FormItem>
        <FormItem label={t('statusProfile')} id='status-profile'>
          <SimpleSelect
            options={statusProfileOptions}
            onChange={setStatusProfile}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onSelect={handleSelect('statusProfile', statusProfile)}
            onDeselect={handleDeselect('statusProfile', statusProfile)}
            onClear={handleClear('statusProfile')}
            mode="multiple"
            value={statusProfile}
            aria-label={t('statusProfileFilter')}
            aria-labelledby='status-profile'
          />
        </FormItem>
      </div>
      <FormItem label={'Staffing'}>
        <Divider className='profile-divider__staffing' />
      </FormItem>
      <div className='profile-filter__container_staffing'>
        <FormItem label={t('cutoffDate')} id='cutoff-date'>
          <TreeSelectCutoff
            options={filters.cutoffMonths}
            onChange={handleCutoffDateChange}
            clear={clearFilter}
            onDropdownVisibleChange={handleDropdownClose}
            onClear={handleClear('cutoffDate')}
            aria-label={t('cutoffDateFilter')}
            aria-labelledby='cutoff-date'
          />
        </FormItem>
        <FormItem label={t('cutoffValue')} id='cutoff-value'>
          <div aria-label={t('cutoffValueFilter')}>
            <CITSlider disabled={isEmptyDate} onChange={setCutoffPercent} />
            {isEmptyDate && (
              <>
                <StopOutlined className='profile-filter__cutoff' />
                <span className='profile-filter__cutoff'>
                  {isEmptyDate && t('cutoffSliderDisabled')}
                </span>
              </>
            )}
          </div>
        </FormItem>
        <FormItem>
          <Button
            type={ButtonType.Clear}
            className='btn-clear-filters'
            onClick={() => setClearFilter(true)}
          >
            <span className='btn-clear-content'>
              <CloseOutlined className='icon-x' />
              <span className='btn-clear-text'>{t('clearFilters')}</span>
            </span>
          </Button>
        </FormItem>
      </div>
    </>
  );
}

Filters.defaultProps = {
  onApiFilter: noop,
  filters: {},
};

export default Filters;